import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { KEEP_EDITING, NotPermitted, ReasonDialog, RecordSection } from '@/design';
import { ApiError } from '@/domains/api';
import { useCan } from '@/domains/auth';
import type {
  IFigureAddress,
  IFigureCell,
  IFigureMetric,
  IFigureTable
} from '../../api/figure-contracts';
import { figuresApi } from '../../api/figures-api';
import { LoadError, TableSkeleton, useAdminData } from '../../components/admin-page';
import { formatDay } from '../../components/days';
import { AdminInlineField, InternalFieldDisplay } from '../../components/field-controls';
import { TableFrame } from '../../components/table-frame';
import { changeText, KEPT_UNDER_WORDS, notKeptHere, SCOPE_WORDS, wordFor } from '../../figures';
import { refusalMessage } from '../../refusal';
import { FigureHistory } from './figures-history';
import { HUB_MEMORY_PAGE } from './hub-memory';
import type {
  IResearchPart,
  IResearchPartSection,
  IResearchWrites,
  IRevealRequest
} from './research';
import { isNotFound, OUT_OF_DATE, rereadsAfter, unanswered } from './said';

/**
 * The hub's **Asset & production** — survey-api's figures table for one member and survey (the
 * advisor record page's S8, W5 and W6, block F5; its plan's R1–R8), drawn inside that member's Veritant
 * research part through the part's slot (`IResearchPartSection`, Q1 ruled (a)) — where the staff
 * form holds its derived figure fields, as Salesforce's page holds "Asset & Production Data".
 *
 * **Every part draws the fold, and the table says where its figures are kept** (block F12's R2 and
 * Q1 ruled (a), over what survey-api `30d88f37` serves): the part reads its own
 * survey's table, and the table's `keptUnder` names the survey the figures are kept under — a
 * `CANADA_FR` part's are `CANADA`'s, which it writes and says ("Kept under the Canada survey"). A
 * survey with no catalogue answers no columns, and the fold says "No figures are kept for <survey>".
 * Rejected: a list of the surveys that keep figures, to draw no fold for the rest — the mirror F5
 * kept while the wire could not tell an unknown participant from a survey with none.
 *
 * **A fold read on its first opening** (`RecordSection`'s `lazy`), closed by default — U7 opens
 * Information, U4 and DD alone — so a household of five does not read ten tables nobody opened. It
 * counts nothing in its summary (`filled={0} total={0}`, Records' way).
 *
 * **The table is what survey-api sent, and nothing is computed** (R3): the columns are `metrics`
 * in the order sent, each its label over its scope in words; the rows are `rows` in the order sent,
 * newest first; a cell is its value as money — the rendering Veritant research gives a derived
 * field, so one number reads alike in both places — with survey-api's `change` under it as a signed
 * percent, muted and in no tone (a change is not a verdict). A cleared cell reads "Cleared"; an
 * absent one nothing but its pencil. Where the value came from and who set it are the cell's
 * history's, never the face's (the roadmap's D11). Nothing is sorted, summed or counted here. It
 * sits in `TableFrame`, so FSP's 24 columns scroll inside the section and never widen the page.
 *
 * **A cell is one `AdminInlineField` of type `money`, and Save asks for a reason** (R5, Q2 ruled
 * (a)): the amount is held as typed and judged by survey-api — no bound, no decimals, no sign
 * checked here; malformed text is held with the shape wanted and sends nothing — and Save and Clear
 * each open `ReasonDialog`, the reason optional and its length unsaid (survey-api bounds none),
 * opening in the reason box with Enter as the press (`enterConfirms` — a cell is Enter, Enter; the
 * owner's ruling of 2026-10-04), whose Cancel answers `KEEP_EDITING`: back to the control, nothing
 * sent. A value saved unchanged
 * sends nothing and asks nothing. The field settles only once the dialog has gone (`onClosed`),
 * F4's way, so its focus rule meets a page with nothing focused. Each cell's history is
 * `FigureHistory` in its `actions` (R6) — none on an absent cell, which has nothing to read.
 *
 * **A write follows the hub's write rules** (R7): a landing holds the answer — the `PUT`'s cell, or
 * a cleared cell after the `DELETE` — over the table as an overlay dropped by **a table read begun
 * after the write landed** (counted here, as Veritant research counts the hub's — never inferred
 * from a promise), then re-reads the hub through `reread`, which moves `written`, which re-reads
 * the table. A 400 is said beside the field with the draft kept and nothing re-read; a 404 says the
 * page is out of date and re-reads; an answer that never arrived is `AdminInlineField`'s sentence,
 * and re-reads, so the table shows whether it landed. A read's 404 is an unknown participant and
 * nothing else (survey-api's D3): said so, and the hub re-read once — the member a re-read no
 * longer carries goes with its part — never again until a read lands, since the hub's re-read
 * moves `written`, which reads the table again. Refusals are told apart by status alone —
 * survey-api's two have no `type` to tell (a 400's `problems[]`, a bodiless 404).
 *
 * **Re-read on `written`, never on the poll** (R7's read half): figures move on a write on this
 * page, a freeze on another, or an import — the hub's poll is about BrokerCheck. The newest read
 * begun is the one kept (`useAdminData`); a re-read that fails over a drawn table says so above it,
 * never as the first load's failure, which is said with "Read again".
 *
 * **A new as-of date is a row this page holds until one of its cells is saved** (R4): "Add an as-of
 * date" draws it at the top, "Not saved yet", every cell an empty pencil; the first cell saved
 * writes the date, and the re-read draws the row where survey-api orders it. A half-typed date is
 * held, and a date already drawn is not added twice — its row's first cell is focused instead.
 * survey-api judges the date itself.
 *
 * **A derived figure field's press shows its cell** (R8): the part hands the request over
 * (`reveal`), and the section opens its fold the section nav's way — `open` set on the `<details>`,
 * which `RecordSection` follows by `toggle` and does not remember — waits for the table, which the
 * fold reads on that opening, then scrolls to the row of the source's date and focuses its cell's
 * pencil. Where the table holds no such row it scrolls to the table and answers "No figure as of
 * <day> — the imported value is shown", which the part says beside the button. Nothing navigates.
 *
 * Rejected: a hub section of its own after Veritant research (Q1's (b)) — two places for a survey's
 * staff data, and a break of U1's order; a reason box inside the cell's editor (Q2's (b)) — a text
 * box in a table cell, cramped on FSP's 24 columns; and recomputing `change` from the rows drawn —
 * a second computation that would disagree with survey-api's the first time a row is not on the
 * page.
 */

/** The section's title — the heading, and the words a derived field's link names. */
const TITLE = 'Asset & production';

/** Asset & production, a block of Veritant research's parts (R1) — what `hubSections()` passes. */
export const figuresPartSection: IResearchPartSection = {
  key: 'figures',
  title: TITLE,
  sourceKind: 'figure',
  render: (part) => <FiguresSection part={part} />
};

function FiguresSection({ part }: { part: IResearchPart }) {
  const { member, surveyCode, surveyName, writes, reveal, revealed } = part;
  const holder = React.useRef<HTMLDivElement>(null);
  // A press opens the fold — and so mounts the table, which reads — the section nav's way. A
  // layout effect, so the fold is open before the table's own effect answers the press: a table
  // still mounted from an earlier opening (`lazy` keeps it) would otherwise scroll to and focus a
  // cell inside a shut `<details>`, where neither does anything.
  React.useLayoutEffect(() => {
    const fold = holder.current?.querySelector('details');
    if (reveal !== null && fold && !fold.open) fold.open = true;
  }, [reveal]);
  return (
    <div ref={holder}>
      <RecordSection
        page={HUB_MEMORY_PAGE}
        sectionKey={`figures.${surveyCode}`}
        title={TITLE}
        level={part.section ? 2 : 4}
        anchor={part.section?.anchor}
        defaultOpen={false}
        filled={0}
        total={0}
        lazy
      >
        {/* RNK-09's word for it (SR-451): the dated figures the rankings read, never the advisor's
            to see — named under the related list's own title, which links and specs name. */}
        <p className="mb-2 text-xs text-muted-foreground" data-metrics-archive="">
          <span className="font-medium text-foreground">Metrics archive</span> — the figures each
          ranking reads at its snapshot: the advisor’s answers, staff edits and firms’ files, each
          dated. Not shown to the advisor.
        </p>
        <FiguresTable
          participantId={member.participantId}
          surveyCode={surveyCode}
          surveyName={surveyName}
          name={member.displayName}
          writes={writes}
          reveal={reveal}
          revealed={revealed}
        />
      </RecordSection>
    </div>
  );
}

/** A write's answer, held over the table until a read begun after it lands (R7). */
interface IHeldCell {
  cell: IFigureCell;
  /** How many table reads had begun when the write landed. */
  after: number;
}

function FiguresTable({
  participantId,
  surveyCode,
  surveyName,
  name,
  writes,
  reveal,
  revealed
}: {
  participantId: string;
  surveyCode: string;
  surveyName: string;
  name: string;
  writes: IResearchWrites;
  reveal: Nullable<IRevealRequest>;
  revealed: (request: IRevealRequest, note: Nullable<string>) => void;
}) {
  const { written, reread } = writes;
  // A cell's write and clear are `internal_fields.write` (SR-228): without it every pencil and
  // "Add an as-of date" are drawn off, under one sentence above the table.
  const permitted = useCan()('internal_fields.write');
  const reasonId = React.useId();
  const notPermitted = permitted ? undefined : reasonId;
  // How many table reads have begun — each read carries its number, so an overlay knows whether
  // the table on screen was read after its write landed (R7).
  const readsBegun = React.useRef(0);
  // Whether a read's 404 has re-read the hub since a read last landed — once, or the re-read,
  // which moves `written`, would read the table into the same 404 for as long as the hub carries
  // the member.
  const goneReread = React.useRef(false);
  const { data, error, reload } = useAdminData(async (signal) => {
    const read = ++readsBegun.current;
    try {
      const table = await figuresApi.table(participantId, surveyCode, signal);
      goneReread.current = false;
      return { table, read };
    } catch (readError) {
      if (isNotFound(readError) && !goneReread.current) {
        goneReread.current = true;
        void reread();
      }
      throw new Error(readFailure(readError, signal));
    }
  }, [participantId, surveyCode, written]);
  const [held, setHeld] = React.useState<ReadonlyMap<string, IHeldCell>>(() => new Map());
  const [newRow, setNewRow] = React.useState<Nullable<string>>(null);

  const frame = React.useRef<HTMLDivElement>(null);
  const answer = React.useRef(revealed);
  answer.current = revealed;
  // A waiting press is answered once a table is drawn — or once the first read has failed, which
  // the section says itself.
  React.useEffect(() => {
    if (reveal === null || (data === null && error === null)) return;
    const table = frame.current;
    // R2: a field derived from another survey's figures has no cell here — said, nothing focused.
    if (data !== null && reveal.source.surveyCode !== data.table.keptUnder) {
      table?.scrollIntoView({ block: 'center' });
      answer.current(reveal, notKeptHere(reveal.source.surveyCode, 'figures'));
      return;
    }
    const { asOf, metric, scope } = reveal.source;
    const row = asOf ? rowOf(table, asOf) : null;
    if (!row) {
      table?.scrollIntoView({ block: 'center' });
      const missing = data !== null && asOf;
      answer.current(
        reveal,
        missing ? `No figure as of ${formatDay(asOf)} — the imported value is shown` : null
      );
      return;
    }
    focusCell(
      row.querySelector<HTMLElement>(`[data-figure-cell="${CSS.escape(`${metric}.${scope}`)}"]`) ??
        row
    );
    answer.current(reveal, null);
  }, [reveal, data, error]);

  // Holds the answer, then re-reads the hub — in that order, so the table read `written` starts is
  // one begun after the overlay was taken.
  const land = (address: IFigureAddress, cell: IFigureCell) => {
    const after = readsBegun.current;
    setHeld((cells) => new Map(cells).set(cellKey(address), { cell, after }));
    void reread();
  };

  if (data === null) {
    return (
      <div ref={frame} data-figures={`${participantId}:${surveyCode}`} className="grid gap-3">
        {error ? (
          <div className="grid justify-items-start gap-2">
            <LoadError message={error} />
            <Button type="button" size="sm" variant="outline" onClick={() => void reload()}>
              Read again
            </Button>
          </div>
        ) : (
          <TableSkeleton rows={2} />
        )}
      </div>
    );
  }

  const { table, read } = data;
  // A cell is written under the survey its figures are kept under — `CANADA` for
  // `CANADA_FR` — never the part's own.
  const addressOf = (metric: IFigureMetric, asOf: string): IFigureAddress => ({
    surveyCode: table.keptUnder,
    metric: metric.metric,
    scope: metric.scope,
    asOf
  });
  // A held answer is drawn while the table on screen was read no later than its write landed.
  const overlay = (address: IFigureAddress) => {
    const kept = held.get(cellKey(address));
    return kept !== undefined && read <= kept.after ? kept.cell : undefined;
  };
  const drawnDays = new Set(table.rows.map((row) => row.asOf));
  // The unsaved row is drawn until a read draws its date (R4).
  const pending = newRow !== null && !drawnDays.has(newRow) ? newRow : null;

  return (
    <div ref={frame} data-figures={`${participantId}:${surveyCode}`} className="grid gap-3">
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Could not refresh — showing the figures as last read</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {table.keptUnder !== surveyCode && (
        <p className="text-sm text-muted-foreground">
          Kept under the {wordFor(KEPT_UNDER_WORDS, table.keptUnder)} survey
        </p>
      )}
      {table.metrics.length === 0 ? (
        <p className="text-sm text-muted-foreground">No figures are kept for {surveyName}.</p>
      ) : (
        <>
          {table.rows.length === 0 && pending === null && (
            <p className="text-sm text-muted-foreground">No figures yet for {surveyName}.</p>
          )}
          {notPermitted && <NotPermitted id={notPermitted} what="changing figures" />}
          <AddAsOf
            disabledFor={notPermitted}
            drawn={(day) => drawnDays.has(day) || day === pending}
            onAdd={setNewRow}
            onDrawn={(day) =>
              focusCell(
                rowOf(frame.current, day === pending ? NEW_ROW : day)?.querySelector<HTMLElement>(
                  '[data-figure-cell]'
                ) ?? null
              )
            }
          />
          <Figures
            table={table}
            pending={pending}
            name={name}
            saved={(asOf) => table.metrics.some((metric) => overlay(addressOf(metric, asOf)))}
            cell={(drawn, metric, asOf, unsaved) => {
              const address = addressOf(metric, asOf);
              return (
                <FigureCellField
                  participantId={participantId}
                  address={address}
                  metric={metric}
                  cell={overlay(address) ?? drawn}
                  name={name}
                  unsaved={unsaved}
                  notPermitted={notPermitted}
                  land={land}
                  reread={reread}
                />
              );
            }}
          />
        </>
      )}
    </div>
  );
}

/** R4's unsaved row's `data-figure-row`. */
const NEW_ROW = 'new';

/** One cell's key among the held answers — its metric, scope and date (the survey is the table's). */
function cellKey({ metric, scope, asOf }: IFigureAddress): string {
  return JSON.stringify([metric, scope, asOf]);
}

/** The row drawn for `asOf` (or `NEW_ROW`) inside the table's frame. */
function rowOf(frame: Nullable<HTMLElement>, asOf: string): Nullable<HTMLElement> {
  return frame?.querySelector<HTMLElement>(`[data-figure-row="${CSS.escape(asOf)}"]`) ?? null;
}

/** Scrolls a cell into view — sideways too, since the table scrolls inside its frame (FSP's 24
 *  columns, or FA's four at a phone's width) and a row's own scroll never moves the frame — then
 *  focuses its pencil ("Edit …", `InlineField`'s name for it) where it has one that is on, else
 *  the cell (`tabIndex={-1}`), which an open editor fills — a disabled pencil takes no focus. */
function focusCell(cell: Nullable<HTMLElement>): void {
  cell?.scrollIntoView({ block: 'center', inline: 'center' });
  const pencil = cell?.querySelector<HTMLElement>('button[aria-label^="Edit "]:not(:disabled)');
  (pencil ?? cell)?.focus({ preventScroll: true });
}

function Figures({
  table,
  pending,
  name,
  saved,
  cell
}: {
  table: IFigureTable;
  /** R4's unsaved as-of date, drawn at the top, or null. */
  pending: Nullable<string>;
  name: string;
  /** Whether a write at that date has landed — an unsaved row is "Not saved yet" until one has. */
  saved: (asOf: string) => boolean;
  cell: (
    cell: Nullable<IFigureCell>,
    metric: IFigureMetric,
    asOf: string,
    unsaved: boolean
  ) => React.ReactNode;
}) {
  const rows = [
    ...(pending === null ? [] : [{ asOf: pending, cells: [], unsaved: true }]),
    ...table.rows.map((row) => ({ ...row, unsaved: false }))
  ];
  return (
    <TableFrame>
      <Table aria-label={`${TITLE} of ${name}`}>
        <TableHeader>
          <TableRow>
            <TableHead>As of</TableHead>
            {table.metrics.map((metric) => (
              <TableHead key={`${metric.metric}.${metric.scope}`} className="align-bottom">
                <MetricHeader metric={metric} />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            // Keyed by its date, the unsaved row too: the re-read that draws its date keeps the
            // row — a neighbour's open draft and the saved cell's focus — rather than drawing it
            // afresh. `NEW_ROW` is the hook alone.
            <TableRow key={row.asOf} data-figure-row={row.unsaved ? NEW_ROW : row.asOf}>
              <TableCell className="align-top whitespace-nowrap">
                <span className="grid">
                  <span>{formatDay(row.asOf)}</span>
                  {row.unsaved && !saved(row.asOf) && (
                    <span className="text-xs text-muted-foreground">Not saved yet</span>
                  )}
                </span>
              </TableCell>
              {table.metrics.map((metric, index) => (
                <TableCell
                  key={`${metric.metric}.${metric.scope}`}
                  data-figure-cell={`${metric.metric}.${metric.scope}`}
                  // Focused by a derived field's link (R8) where its pencil is not drawn.
                  tabIndex={-1}
                  className="min-w-40 align-top"
                >
                  {cell(row.cells[index] ?? null, metric, row.asOf, row.unsaved)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableFrame>
  );
}

/** A column's head: the metric's label, its scope in words beneath (the label carries none). */
function MetricHeader({ metric }: { metric: IFigureMetric }) {
  return (
    <span className="grid">
      <span>{metric.label}</span>
      <span className="text-xs font-normal text-muted-foreground">
        {wordFor(SCOPE_WORDS, metric.scope)}
      </span>
    </span>
  );
}

/** One cell's face: its value as money and survey-api's change beneath, "Cleared", or nothing. */
function FigureValue({ cell }: { cell: Nullable<IFigureCell> }) {
  if (cell === null) return null;
  if (cell.value === null) return <span className="text-muted-foreground">Cleared</span>;
  return (
    <span className="grid whitespace-nowrap">
      <InternalFieldDisplay payload={{ t: 'money', v: cell.value }} />
      {cell.change !== null && (
        <span data-change className="text-xs text-muted-foreground">
          {changeText(cell.change)}
        </span>
      )}
    </span>
  );
}

/** R4's "Add an as-of date": a date input and "Add". A half-typed date is held; a date the table
 *  already draws is said and its row's first cell focused, never added twice. */
function AddAsOf({
  disabledFor,
  drawn,
  onAdd,
  onDrawn
}: {
  /** The id of the `NotPermitted` sentence saying why the press is off; absent, it is on. */
  disabledFor?: string;
  drawn: (day: string) => boolean;
  onAdd: (day: string) => void;
  onDrawn: (day: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [day, setDay] = React.useState('');
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const input = React.useRef<HTMLInputElement>(null);
  const inputId = React.useId();

  const close = () => {
    setOpen(false);
    setSaid(null);
    setDay('');
  };

  if (!open) {
    return (
      <div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabledFor !== undefined}
          aria-describedby={disabledFor}
          onClick={() => setOpen(true)}
        >
          Add an as-of date
        </Button>
      </div>
    );
  }

  const add = () => {
    // A half-typed date reaches React as `''`, exactly as an empty box does; the element knows.
    if (input.current?.validity.badInput || day === '') {
      setSaid('Enter a full date.');
      input.current?.focus();
      return;
    }
    if (drawn(day)) {
      setSaid(`${formatDay(day)} is already a row.`);
      onDrawn(day);
      return;
    }
    onAdd(day);
    close();
  };

  return (
    <div className="grid justify-items-start gap-1.5">
      <div className="flex flex-wrap items-end gap-2">
        <div className="grid gap-1 text-sm">
          <label htmlFor={inputId} className="text-muted-foreground">
            New as-of date
          </label>
          <Input
            ref={input}
            id={inputId}
            type="date"
            autoFocus
            className="w-auto"
            value={day}
            onChange={(event) => {
              setDay(event.target.value);
              setSaid(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                add();
              }
            }}
          />
        </div>
        <Button type="button" size="sm" onClick={add}>
          Add
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={close}>
          Cancel
        </Button>
      </div>
      {said && (
        <p role="alert" className="text-sm text-destructive">
          {said}
        </p>
      )}
    </div>
  );
}

/** What the reason dialog is open for: an amount to set, or a clear. */
type Asked = { value: number } | { clear: true };

/** How a reason dialog ended: cancelled, the write landed, or survey-api's refusal. */
type Outcome = { kind: 'kept' } | { kind: 'landed' } | { kind: 'refused'; error: unknown };

/**
 * One cell, read and edited in place (R5–R7). The field's `write` and `clear` each ask for a reason
 * first and settle once the dialog has gone: `undefined` when the write landed, `KEEP_EDITING` when
 * the dialog was cancelled, or the refusal thrown for `AdminInlineField` to say beside the field.
 */
function FigureCellField({
  participantId,
  address,
  metric,
  cell,
  name,
  unsaved,
  notPermitted,
  land,
  reread
}: {
  participantId: string;
  address: IFigureAddress;
  metric: IFigureMetric;
  cell: Nullable<IFigureCell>;
  name: string;
  /** In R4's unsaved row — no history to read yet. */
  unsaved: boolean;
  /** The id of the sentence saying why the pencil is off (`InlineField`'s); absent, it is on. */
  notPermitted?: string;
  land: (address: IFigureAddress, cell: IFigureCell) => void;
  reread: () => Promise<boolean>;
}) {
  const scope = wordFor(SCOPE_WORDS, metric.scope);
  const label = `${metric.label}, ${scope}, as of ${formatDay(address.asOf)}`;
  const [asked, setAsked] = React.useState<Nullable<Asked>>(null);
  const [open, setOpen] = React.useState(false);
  // The field's write waits on the dialog: it is settled with `outcome` once the dialog has gone.
  const settle = React.useRef<Nullable<() => void>>(null);
  const outcome = React.useRef<Outcome>({ kind: 'kept' });

  const ask = (question: Asked): Promise<unknown> =>
    new Promise((resolve, reject) => {
      outcome.current = { kind: 'kept' };
      settle.current = () => {
        const told = outcome.current;
        if (told.kind === 'kept') resolve(KEEP_EDITING);
        else if (told.kind === 'landed') resolve(undefined);
        else reject(told.error);
      };
      setAsked(question);
      setOpen(true);
    });

  const confirm = async (reason: string): Promise<Nullable<string>> => {
    if (asked === null) return null;
    try {
      if ('value' in asked) {
        land(address, await figuresApi.set(participantId, address, asked.value, reason));
      } else {
        await figuresApi.clear(participantId, address, reason);
        land(address, clearedCell());
      }
      outcome.current = { kind: 'landed' };
    } catch (error) {
      // Not survey-api's answer: a dropped connection, or this side's own fault — said to staff as
      // an answer that did not arrive, and kept here for whoever debugs it.
      if (!(error instanceof ApiError)) console.error(error);
      if (rereadsAfter(error)) void reread();
      outcome.current = { kind: 'refused', error };
    }
    // Closes the dialog: what was said belongs beside the cell, with the draft kept.
    return null;
  };

  const onClosed = () => {
    const done = settle.current;
    settle.current = null;
    setAsked(null);
    done?.();
  };

  const clearing = asked !== null && 'clear' in asked;

  return (
    <>
      <AdminInlineField
        field={{
          code: `figure.${address.surveyCode}.${address.metric}.${address.scope}.${address.asOf}`,
          label,
          type: 'money'
        }}
        name={name}
        value={cell?.value == null ? null : { t: 'money', v: cell.value }}
        display={<FigureValue cell={cell} />}
        // A `money` field's payload: `encodeInternalValue` hands over a number.
        write={(payload) => ask({ value: payload.v as number })}
        clear={() => ask({ clear: true })}
        refusal={refusalOf}
        notPermitted={notPermitted}
        actions={
          cell !== null && !unsaved ? (
            <FigureHistory
              participantId={participantId}
              address={address}
              label={label}
              name={name}
            />
          ) : undefined
        }
      />
      {/* Mounted for one write at a time, not once per cell of the table. */}
      {asked && (
        <ReasonDialog
          open={open}
          onOpenChange={setOpen}
          onClosed={onClosed}
          title={clearing ? 'Clear this figure' : 'Set this figure'}
          description={
            `${label} for ${name}. The system keeps the reason with the change, in the figure’s ` +
            'history.'
          }
          required={false}
          enterConfirms
          confirmLabel={clearing ? 'Clear it' : 'Save it'}
          onConfirm={confirm}
        />
      )}
    </>
  );
}

/** The cell a `DELETE` leaves — the row kept, its value null — held until the re-read lands. Only
 *  its value is drawn on the table's face. */
function clearedCell(): IFigureCell {
  return {
    value: null,
    origin: 'staff',
    setAt: new Date().toISOString(),
    actor: null,
    source: null,
    change: null
  };
}

/** A failed table read in words (R7's read half). survey-api's 404 is bodiless, and the client's
 *  own message for one ("Request failed with status 404") says nothing to staff: it is an unknown
 *  participant and nothing else (F12's R3), so the page is out of date. An answer that never
 *  arrived — a dropped connection, a 5xx, or a 2xx that carried no table — is said plainly and
 *  kept in the console for whoever debugs it; an abort is no failure, and `useAdminData` drops it.
 *  Any other refusal is survey-api's words — a 400 `invalid-query` at `survey` among them, which
 *  the page cannot provoke: it reads only the surveys the hub's responses name. */
function readFailure(error: unknown, signal: AbortSignal): string {
  if (isNotFound(error)) {
    return 'The system holds no such participant any more — the page may be out of date.';
  }
  if (unanswered(error)) {
    if (!(error instanceof ApiError) && !signal.aborted) console.error(error);
    return 'The figures could not be read.';
  }
  return refusalMessage(error, 'The figures could not be read.');
}

/** A refusal in words, by status (R7): a bodiless 404 is the page out of date; anything else is
 *  `refusalMessage`'s — a 400's `problems[]` at `value` in survey-api's own words. An answer that
 *  never arrived is `AdminInlineField`'s to say. */
function refusalOf(error: unknown): string {
  if (isNotFound(error)) {
    return `This figure is no longer where the page put it. ${OUT_OF_DATE}`;
  }
  return refusalMessage(error, 'The figure could not be saved.');
}
